/* eslint-disable @angular-eslint/directive-selector */
import {
  ChangeDetectorRef,
  Directive,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Entry } from 'contentful';
import {
  ContentTypes,
  ContentfulDataService,
} from '@nibtravel/shared/data-access/contentful';

export interface ContentfulContext {
  // to enable `let` syntax we have to use $implicit (var; let v = var)
  $implicit: Entry<any> | undefined;
  // to enable `as` syntax we have to assign the directives selector (var as v)
  contentful: Entry<any> | undefined;
}

/**
 * Directive will render content if content entry is found.
 *
 * @export
 * @class ContentfulDirective
 */
@Directive({
  selector: '[contentful]',
})
export class ContentfulDirective implements OnChanges, OnDestroy {
  static ngTemplateGuard_contentful: 'binding';
  @Input() contentful!: ContentTypes;
  @Input('contentfulZone') zone!: string;
  @Input() contentfulAllowFallbackBrand!: boolean;
  private destroy$ = new Subject<boolean>();
  private contentChanged$ = new Subject<{
    contentType: ContentTypes;
    zone: string;
    allowFallbackBrand: boolean;
  }>();
  private readonly viewContext: ContentfulContext = {
    $implicit: undefined,
    contentful: undefined,
  };

  constructor(
    private viewContainer: ViewContainerRef,
    private templateRef: TemplateRef<ContentfulContext>,
    private changeDetectorRef: ChangeDetectorRef,
    contentfulDataService: ContentfulDataService
  ) {
    this.contentChanged$
      .pipe(
        switchMap((params) => {
          return contentfulDataService.getContent(params.contentType, {
            zone: params.zone,
            allowFallbackBrand: params.allowFallbackBrand,
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (entry) => {
          this.viewContext.$implicit = entry;
          this.viewContext.contentful = entry;
          this.updateView();
        },
        (error) => {
          console.log('Error', error);
        }
      );
  }

  static ngTemplateContextGuard(
    dir: ContentfulDirective,
    ctx: unknown | null | undefined
  ): ctx is ContentfulDirective {
    return true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.contentful ||
      changes.zone ||
      changes.contentfulAllowFallbackBrand
    ) {
      this.contentChanged$.next({
        contentType: changes.contentful?.currentValue,
        zone: changes.zone?.currentValue,
        allowFallbackBrand: changes.contentfulAllowFallbackBrand?.currentValue,
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private updateView(): void {
    if (this.viewContext.$implicit) {
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef, this.viewContext);
    } else {
      this.viewContainer.clear();
    }
    this.changeDetectorRef.markForCheck();
  }
}
